import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader, DataTable, EmptyState } from '@ui-kit';
import type { DataTableColumn } from '@ui-kit';
import { GatewayConfirmDialog } from '../../_shared/components/GatewayConfirmDialog';
import { ComingSoonState } from '../../_shared/components/ComingSoonState';
import { CertificateFormDialog } from './CertificateFormDialog';
import { useCertificatesQuery, useDeleteCertificate } from '../hooks/useCertificates';
import type { CertificateRow, CertificateFilters } from '../types/certificate';

function TableSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} style={{ height: '48px', background: 'var(--color-surface-muted)', borderRadius: 'var(--radius-md)' }} />
      ))}
    </div>
  );
}

function maskFingerprint(fp?: string): string {
  if (!fp) return '-';
  return fp.length > 12 ? `${fp.slice(0, 8)}...${fp.slice(-4)}` : fp;
}

export function CertificatesListPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<CertificateFilters>({});
  const [page, setPage] = useState(0);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | null>(null);
  const [selectedCertificate, setSelectedCertificate] = useState<CertificateRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CertificateRow | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const GATEWAY_ENABLED = false;
  const { data, isLoading, isError } = useCertificatesQuery(filters, page, { enabled: GATEWAY_ENABLED });
  const deleteCertificate = useDeleteCertificate(() => setDeleteTarget(null));

  const columns: DataTableColumn<CertificateRow>[] = [
    { key: 'cn', header: t('gateway.certificates.columns.cn'), width: '220px' },
    {
      key: 'sni',
      header: t('gateway.certificates.columns.sni'),
      width: '160px',
      render: (row) => (
        <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>
          {row.sni ?? '-'}
        </span>
      ),
    },
    {
      key: 'expiresAt',
      header: t('gateway.certificates.columns.expiresAt'),
      width: '120px',
      render: (row) => {
        if (!row.expiresAt) return <span>-</span>;
        const exp = new Date(row.expiresAt);
        const isExpired = exp < new Date();
        return (
          <span style={{ color: isExpired ? 'var(--color-danger)' : 'var(--color-text-primary)', fontSize: 'var(--font-size-xs)' }}>
            {exp.toLocaleDateString('ko-KR')}
          </span>
        );
      },
    },
    {
      key: 'fingerprint',
      header: t('gateway.certificates.columns.fingerprint'),
      width: '140px',
      render: (row) => (
        <span
          title={row.fingerprint}
          style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}
        >
          {maskFingerprint(row.fingerprint)}
        </span>
      ),
    },
    {
      key: 'enabled',
      header: t('gateway.certificates.columns.enabled'),
      width: '80px',
      render: (row) => (
        <span
          style={{
            padding: 'var(--spacing-1) var(--spacing-2)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--font-size-xs)',
            background: row.enabled ? 'var(--color-success-subtle)' : 'var(--color-surface-muted)',
            color: row.enabled ? 'var(--color-success)' : 'var(--color-text-secondary)',
          }}
        >
          {row.enabled ? '활성' : '비활성'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t('gateway.certificates.columns.actions'),
      width: '160px',
      render: (row) => (
        <div style={{ display: 'flex', gap: 'var(--spacing-1)' }}>
          <button
            type="button"
            aria-label={`${row.cn} ${t('gateway.shared.actions.edit')}`}
            onClick={(e) => { e.stopPropagation(); setSelectedCertificate(row); setDialogMode('edit'); }}
            style={actionBtnStyle}
          >
            {t('gateway.shared.actions.edit')}
          </button>
          <button
            type="button"
            aria-label={`${row.cn} ${t('gateway.shared.actions.delete')}`}
            onClick={(e) => { e.stopPropagation(); setDeleteTarget(row); }}
            style={{ ...actionBtnStyle, color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}
          >
            {t('gateway.shared.actions.delete')}
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      {toast && (
        <div
          role="alert"
          style={{
            position: 'fixed',
            top: 'var(--spacing-4)',
            right: 'var(--spacing-4)',
            padding: 'var(--spacing-3) var(--spacing-4)',
            background: 'var(--color-success)',
            color: 'var(--color-text-inverse)',
            borderRadius: 'var(--radius-md)',
            zIndex: 3000,
            fontSize: 'var(--font-size-sm)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {toast}
        </div>
      )}

      <div
        role="note"
        aria-label={t('gateway.certificates.sidebarHiddenNote')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          padding: 'var(--spacing-2) var(--spacing-4)',
          background: 'var(--color-info-subtle, #EEF2FF)',
          border: '1px solid var(--color-info-border, #C7D2FE)',
          borderBottom: 'none',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-info-text, #3730A3)',
        }}
      >
        <span aria-hidden="true">i</span>
        <span>{t('gateway.certificates.sidebarHiddenNote')}</span>
      </div>

      <PageHeader
        title={t('gateway.certificates.title')}
        breadcrumb={t('gateway.certificates.breadcrumb')}
        actions={
          <button
            type="button"
            disabled={!GATEWAY_ENABLED}
            title={!GATEWAY_ENABLED ? t('gateway.certificates.actions.createComingSoon') : undefined}
            aria-disabled={!GATEWAY_ENABLED}
            onClick={() => {
              if (!GATEWAY_ENABLED) return;
              setSelectedCertificate(null);
              setDialogMode('create');
            }}
            style={{
              ...primaryBtnStyle,
              opacity: !GATEWAY_ENABLED ? 0.5 : 1,
              cursor: !GATEWAY_ENABLED ? 'not-allowed' : 'pointer',
            }}
          >
            {t('gateway.certificates.actions.create')}
          </button>
        }
      />

      {/* Filter Bar */}
      <div style={{ display: 'flex', gap: 'var(--spacing-3)', padding: 'var(--spacing-4) var(--spacing-6)', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="search"
          aria-label={t('gateway.certificates.filter.search')}
          placeholder={t('gateway.certificates.filter.search')}
          value={filters.q ?? ''}
          onChange={(e) => setFilters({ ...filters, q: e.target.value || undefined })}
          style={{ ...selectStyle, flex: 1, minWidth: '200px' }}
        />
      </div>

      {/* Main Content */}
      <div style={{ padding: '0 var(--spacing-6) var(--spacing-6)' }}>
        {!GATEWAY_ENABLED ? (
          <ComingSoonState />
        ) : isLoading ? (
          <TableSkeleton />
        ) : isError ? (
          <ComingSoonState />
        ) : !data || data.content.length === 0 ? (
          <EmptyState
            title={t('gateway.certificates.list.empty')}
            description={t('gateway.certificates.list.emptyDesc')}
            action={
              !GATEWAY_ENABLED ? undefined : (
                <button type="button" onClick={() => { setSelectedCertificate(null); setDialogMode('create'); }} style={primaryBtnStyle}>
                  {t('gateway.certificates.list.emptyAction')}
                </button>
              )
            }
          />
        ) : (
          <>
            <DataTable columns={columns} rows={data.content} rowKey={(row) => row.id} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'var(--spacing-4)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
              <span>
                {t('gateway.shared.pagination.range', {
                  total: data.totalElements,
                  start: data.number * data.size + 1,
                  end: Math.min((data.number + 1) * data.size, data.totalElements),
                })}
              </span>
              <div style={{ display: 'flex', gap: 'var(--spacing-1)' }}>
                <button type="button" disabled={data.first} onClick={() => setPage((p) => p - 1)} style={pageBtnStyle}>
                  &laquo; {t('gateway.shared.pagination.prev')}
                </button>
                <span style={{ padding: 'var(--spacing-1) var(--spacing-2)' }}>{data.number + 1} / {data.totalPages}</span>
                <button type="button" disabled={data.last} onClick={() => setPage((p) => p + 1)} style={pageBtnStyle}>
                  {t('gateway.shared.pagination.next')} &raquo;
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {dialogMode && (
        <CertificateFormDialog
          open={!!dialogMode}
          mode={dialogMode}
          initial={selectedCertificate}
          onClose={() => setDialogMode(null)}
          onSaved={() => {
            setToast(dialogMode === 'create' ? t('gateway.certificates.toast.created', { name: selectedCertificate?.cn ?? '' }) : t('gateway.certificates.toast.updated'));
            setTimeout(() => setToast(null), 3000);
            setDialogMode(null);
          }}
        />
      )}

      {deleteTarget && (
        <GatewayConfirmDialog
          open={!!deleteTarget}
          title={t('gateway.certificates.delete.confirm')}
          description={t('gateway.certificates.delete.description')}
          destructive
          confirmLabel={t('gateway.shared.actions.delete')}
          onConfirm={() => deleteCertificate.mutate(deleteTarget.id)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

const primaryBtnStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  background: 'var(--color-brand-primary)',
  color: 'var(--color-text-inverse)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
};

const actionBtnStyle: React.CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-2)',
  background: 'var(--color-surface-background)',
  color: 'var(--color-text-secondary)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-xs)',
};

const selectStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  background: 'var(--color-surface-background)',
};

const pageBtnStyle: React.CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-2)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--color-surface-background)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-sm)',
};
